import React, { useCallback, useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import "../../styles/AdminPage.css";
import Button, { BUTTON_TYPES } from '../Button';
import MultiRangeSlider from "../inputs/MultiRangeSlider";
import PropTypes from 'prop-types';
import { FaTimes } from "react-icons/fa";
import LabeledCheckbox from '../inputs/LabeledCheckBox';

Modal.setAppElement('#root');

const CommitteeFilterModal = ({ isOpen, onClose, initialFormData, onFormDataChange, onApply }) => {
    const [isArchiveEnabled, setIsArchiveEnabled] = useState(() =>
        initialFormData?.isArchiveEnabled || false
    );
    const [activeMembersRange, setActiveMembersRange] = useState(initialFormData?.activeMembersRange || { min: 0, max: 1000 });
    const [membersRange, setMembersRange] = useState(initialFormData?.membersRange || { min: 0, max: 1000 });
    const [activeMembersResetKey, setActiveMembersResetKey] = useState(0);
    const [membersResetKey, setMembersResetKey] = useState(0);

    const didInit = useRef(false);

    const onFormDataChangeRef = useRef(onFormDataChange);
    useEffect(() => {
        onFormDataChangeRef.current = onFormDataChange;
    }, [onFormDataChange]);

    useEffect(() => {
        if (isOpen) {
            if (!didInit.current) {
                setActiveMembersRange(initialFormData?.activeMembersRange || { min: 0, max: 1000 });
                setMembersRange(initialFormData?.membersRange || { min: 0, max: 1000 });
                setIsArchiveEnabled(initialFormData?.isArchiveEnabled || false);
                didInit.current = true;
            }
        } else {
            didInit.current = false;
        }
    }, [isOpen, initialFormData]);

    useEffect(() => {
        if (onFormDataChangeRef.current) {
            onFormDataChangeRef.current({ activeMembersRange, membersRange, isArchiveEnabled });
        }
    }, [activeMembersRange, membersRange, isArchiveEnabled]);

    const handleActiveMembersChange = useCallback(({ min, max }) => {
        setActiveMembersRange({ min, max });
    }, []);

    const handleMembersChange = useCallback(({ min, max }) => {
        setMembersRange({ min, max });
    }, []);

    const handleReset = () => {
        setActiveMembersRange({ min: 0, max: 1000 });
        setMembersRange({ min: 0, max: 1000 });
        setActiveMembersResetKey(prev => prev + 1);
        setMembersResetKey(prev => prev + 1);
        setIsArchiveEnabled(false);
    };

    const handleApply = () => {
        if (onApply) {
            onApply({ activeMembersRange, membersRange, isArchiveEnabled });
        }
        onClose();
    };

    const handleArchiveChange = (event) => {
        setIsArchiveEnabled(event.target.checked);
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Committee Filter Modal"
            className="custom-modal-content"
            overlayClassName="custom-modal-overlay"
        >
            <>
                <div className='d-flex flex-row justify-content-between align-items-center mb-3'>
                    <h2 className="text-start mb-0">Filter Committees</h2>
                    <div className='d-flex flex-row justify-content-end align-items-center me-1'>
                        <div className='me-4'>
                            <LabeledCheckbox
                                id="archive-checkbox"
                                label="Include Archived Committees?: "
                                checked={isArchiveEnabled}
                                onChange={handleArchiveChange}
                                disabled={false}
                            />
                        </div>
                        <button className="close-btn" style={{ marginTop: '-4px' }} onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>
                </div>
                <MultiRangeSlider
                    key={`active-members-${activeMembersResetKey}`}
                    min={0}
                    max={1000}
                    initialMin={activeMembersRange.min}
                    initialMax={activeMembersRange.max}
                    onChange={handleActiveMembersChange}
                    label='Active Members'
                />
                <MultiRangeSlider
                    key={`members-${membersResetKey}`}
                    min={0}
                    max={1000}
                    initialMin={membersRange.min}
                    initialMax={membersRange.max}
                    onChange={handleMembersChange}
                    label='Members'
                />
                <div className='d-flex flex-row justify-content-center align-items-center two-button-container'>
                    <div className='me-3'>
                        <Button
                            id="reset-button"
                            type={BUTTON_TYPES.outline}
                            buttonText="Reset"
                            action={handleReset}
                        />
                    </div>
                    <Button
                        id="filter-button-modal"
                        type={BUTTON_TYPES.primary}
                        buttonText="Apply"
                        action={handleApply}
                    />
                </div>
            </>
        </Modal>
    );
};

CommitteeFilterModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    initialFormData: PropTypes.object,
    onFormDataChange: PropTypes.func,
    onApply: PropTypes.func
};

export default CommitteeFilterModal;
